import { useMsal } from '@azure/msal-react';
import { useAppConfig } from '../appConfig/appConfig';
import { MsalAuthErrorCode } from './types';
import { useAuthInternal } from './useAuthInternal';
import { useLoginRequest } from './useLoginRequest';
/**
 * Convenience hook that returns a function acquires the user's API token,
 * request is pre-configured.
 *
 * @example
 * ```tsx
 * const myApiCall = ()=>{
 *   const acquireToken = useAcquireToken();
 *
 *   const makeApiCall () => {
 *     const token = await acquireToken();
 *     // make api call with token
 *     return myApiCall({
 *       headers: {
 *         Authorization: token? undefined : `Bearer ${token}`,
 *       },
 *     })
 *   }
 * }
 * ```
 *
 * @returns a function that acquires the user's API token
 */
export const useAcquireToken = () => {
    const { instance } = useMsal();
    const loginRequest = useLoginRequest();
    const { usePopupFlow } = useAppConfig();
    const { setError, setErrorSilent } = useAuthInternal();
    return (additionalRequest) => instance
        .acquireTokenSilent(Object.assign(Object.assign({}, loginRequest), additionalRequest))
        .catch((error) => {
        if (error.errorCode === MsalAuthErrorCode.interaction_required) {
            if (usePopupFlow) {
                // B2C says they need an interactive login, and this is a popup app
                // so we'll use the popup flow.
                return instance
                    .acquireTokenPopup(Object.assign(Object.assign({}, loginRequest), additionalRequest))
                    .catch(error => {
                    console.log('popup error');
                    setError(error);
                    throw error;
                });
            }
            else {
                // Normally you'd put something like this:
                // instance.acquireTokenRedirect({
                //   ...loginRequest,
                //   ...additionalRequest,
                // });
                // But this can lead to users stuck in an endless loop of redirecting
                // back and forth between the login page and the application.
                //
                // TODO: maybe add this but make it also include the number of round
                // trips to the login page in a state value so we don't loop.
            }
        }
        setErrorSilent(error);
        throw error;
    });
};
